// © 2025 Ananda Kumar. All rights reserved.
// Unauthorized copying of this file, via any medium, is strictly prohibited.

import { Component, EventEmitter, Input, Output } from "@angular/core";
import { ToastService } from '../../../services/toast.service';
import { LoadingService } from "../../../services/loading.service";

@Component({
  selector: 'app-upload-modal',
  standalone: false,
  styleUrls: ['./upload-modal.component.css'],
  templateUrl: './upload-modal.component.html'
})
export class UploadModalComponent {
  //@Input('selectedFiles') selectedFiles:any[] = [];
  @Input('headerText') headerText: string = '';
  @Input('headerSubText') headerSubText: string = '';
  @Output('onUpload') onUpload: EventEmitter<File[]> = new EventEmitter<File[]>();
  @Output('onCancel') onCancel: EventEmitter<any> = new EventEmitter<any>();

  selectedFiles: { file: File, preview: any }[] = [];
  maxFiles = 10;
  maxTotalSize = 300 * 1024 * 1024;

  constructor(private toastService: ToastService, private loader: LoadingService) { }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files) return;

    const files = Array.from(input.files);
    const totalSize = files.reduce((acc, file) => acc + file.size, 0);

    if (files.length > this.maxFiles) {
      this.toastService.info(`You can select up to ${this.maxFiles} files.`);
      return;
    }

    if (totalSize > this.maxTotalSize) {
      this.toastService.info(`Total file size must be under 300MB.`);
      return;
    }

    this.loader.show();
    let completed = 0;

    files.forEach(file => {
      this.compressImage(file, 5 * 1024 * 1024) // target max 5MB
        .then(compressedFile => {
          const reader = new FileReader();

          reader.onload = (e: any) => {
            const fileWithPreview = {
              file: compressedFile,
              preview: e.target.result
            };
            this.selectedFiles.push(fileWithPreview);
          };

          reader.onloadend = () => {
            completed++;
            if (completed === files.length) this.loader.hide();
          };

          reader.onerror = () => {
            completed++;
            console.error('Error generating preview:', file.name);
            if (completed === files.length) this.loader.hide();
          };

          reader.readAsDataURL(compressedFile);
        })
        .catch(err => {
          completed++;
          console.error('Compression failed for:', file.name, err);
          if (completed === files.length) this.loader.hide();
        });
    });
  }

  compressImage(file: File, maxSize: number): Promise<File> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e: any) => {
        const img = new Image();
        img.src = e.target.result;

        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          const MAX_WIDTH = 1920;
          const scale = Math.min(1, MAX_WIDTH / img.width);
          canvas.width = img.width * scale;
          canvas.height = img.height * scale;

          ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);

          let quality = 0.9;

          const tryCompress = () => {
            canvas.toBlob(blob => {
              if (!blob) return reject('Compression failed');

              if (blob.size <= maxSize || quality < 0.1) {
                resolve(new File([blob], file.name, { type: file.type }));
              } else {
                quality -= 0.1;
                tryCompress();
              }
            }, file.type, quality);
          };

          tryCompress();
        };

        img.onerror = err => reject(err);
      };

      reader.onerror = err => reject(err);
      reader.readAsDataURL(file);
    });
  }



  removeFile(index: number): void {
    this.selectedFiles.splice(index, 1);
  }

  onUploadClick() {
    this.onUpload.emit(this.selectedFiles.map(e => e.file));
  }

  cancel() {
    this.onCancel.emit('cancel');
  }
}